import { logReportDownload } from '../../util/auditLogger';
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { Header } from "../../components/modules";
import { FiFileText, FiArrowLeft } from "react-icons/fi";
import Context from "../../Context";
import { Spinner } from "reactstrap";
import * as xlsx from "xlsx";
import { thaiDate } from "../../util";

export const ReportDailySaleHourly = () => {
  const { db } = Context();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const date = searchParams.get("date");
  const hour = searchParams.get("hour");

  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  const getOrders = async () => {
    if (!date || hour === null) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, "Orders"),
        where("createdAtDate", "==", date),
        where("status", "==", "approved")
      );
      const querySnapshot = await getDocs(q);
      const fetchedOrders = [];
      querySnapshot.forEach((doc) => {
        const order = { id: doc.id, ...doc.data() };
        const dateObj = new Date(order.createdAt);
        if (dateObj.getHours() === Number(hour)) {
          fetchedOrders.push(order);
        }
      });

      // Sort chronologically (ascending)
      fetchedOrders.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setOrders(fetchedOrders);
    } catch (err) {
      console.error("Failed to fetch hourly orders:", err);
      window.alert("เกิดข้อผิดพลาดในการดึงข้อมูลคำสั่งซื้อ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, hour]);

  const formatTime = (createdAt) => {
    if (!createdAt) return "-";
    const d = new Date(createdAt);
    const h = String(d.getHours()).padStart(2, "0");
    const m = String(d.getMinutes()).padStart(2, "0");
    const s = String(d.getSeconds()).padStart(2, "0");
    return `${h}:${m}:${s} น.`;
  };

  const hourLabel = hour !== null
    ? `${String(hour).padStart(2, "0")}:00 - ${String(Number(hour) + 1).padStart(2, "0")}:00 น.`
    : "";

  const exportToXLSX = () => {
    if (orders.length === 0) return;

    const headers = [
      "ลำดับ",
      "เวลาสั่งซื้อ",
      "เลขที่คำสั่งซื้อ",
      "ชื่อ - นามสกุล",
      "เบอร์โทรศัพท์",
      "LINE Name",
      "LINE ID",
      "จำนวนสลาก (ใบ)",
      "ยอดขาย (บาท)"
    ];

    const dataRows = orders.map((order, index) => [
      index + 1,
      formatTime(order.createdAt),
      order.shortId || order.id || "-",
      `${order.firstName || ""} ${order.lastName || ""}`.trim() || "-",
      order.phone || "-",
      order.lineTitle || "-",
      order.lineId || "-",
      order.size || 0,
      order.totalPrice || 0
    ]);

    const titleRow = [`รายงานคำสั่งซื้อรายชั่วโมง วันที่ ${date || "-"} ช่วงเวลา ${hourLabel}`];
    const emptyRow = [];
    const csvData = [
      titleRow,
      emptyRow,
      headers,
      ...dataRows
    ];

    const ws = xlsx.utils.aoa_to_sheet(csvData);
    const wb = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(wb, ws, "Sheet1");
    logReportDownload();
    xlsx.writeFile(
      wb,
      `รายงานยอดขายรายชั่วโมง_${date}_${hourLabel.replace(/ /g, "")}_ออก_ณ_วันที่${thaiDate()}.xlsx`
    );
  };

  return (
    <div className="h-fit w-full max-w-[1600px] mb-24">


      <Header
        header="Report"
        subHeader={`รายละเอียดคำสั่งซื้อรายชั่วโมง วันที่ ${date || "-"} (${hourLabel})`}
        icon={<FiFileText className="text-blue" />}
      />

      <div className="relative bg-white w-full rounded-lg shadow-md mt-6 border border-[#E5E7E9] overflow-hidden">
        <div className="p-3 border-b border-[#E5E7E9] flex items-center justify-between">
          <p className="font-semibold">รายการคำสั่งซื้อทั้งหมด ({orders.length} ออเดอร์)</p>
          <div className="flex gap-2">
            <button
              disabled={loading || orders.length === 0}
              className="flex font-semibold items-center gap-1 py-1.5 px-3 bg-green text-white text-xs rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:opacity-45 cursor-pointer"
              onClick={exportToXLSX}
            >
              ดาวน์โหลด Excel (.xlsx)
            </button>
            <button
              disabled={loading}
              className="flex font-semibold items-center gap-1 py-1.5 px-3 bg-blue text-white text-xs rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:opacity-45 cursor-pointer"
              onClick={getOrders}
            >
              {loading ? <Spinner className="w-3 h-3" /> : "รีเฟรชข้อมูล"}
            </button>
          </div>
        </div>

        <div className="relative min-h-[300px]">
          {loading && (
            <div className="absolute w-full h-full flex justify-center items-center bg-white bg-opacity-70 z-20">
              <Spinner className="w-8 h-8 text-blue" />
            </div>
          )}

          <Table>
            <TableHeader className="bg-[#f2f3f5]">
              <TableRow>
                <TableHead className="text-center w-16">ลำดับ</TableHead>
                <TableHead className="text-center w-28">เวลาสั่งซื้อ</TableHead>
                <TableHead className="text-center w-36">เลขที่คำสั่งซื้อ (Short ID)</TableHead>
                <TableHead className="w-48">ชื่อ - นามสกุล</TableHead>
                <TableHead className="text-center w-36">เบอร์โทรศัพท์</TableHead>
                <TableHead className="text-center w-36">LINE Name</TableHead>
                <TableHead className="text-center w-36">LINE ID</TableHead>
                <TableHead className="text-center w-24">จำนวนสลาก (ใบ)</TableHead>
                <TableHead className="text-center w-32">ยอดขาย (บาท)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length > 0 ? (
                orders.map((order, index) => (
                  <TableRow key={order.id}>
                    <TableCell className="text-center font-medium">{index + 1}</TableCell>
                    <TableCell className="text-center font-medium">{formatTime(order.createdAt)}</TableCell>
                    <TableCell className="text-center font-semibold font-mono">
                      <a
                        href={`/orders/${order.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue hover:underline cursor-pointer"
                      >
                        {order.shortId || order.id}
                      </a>
                    </TableCell>
                    <TableCell className="font-medium">
                      {order.firstName || ""} {order.lastName || ""}
                      {(!order.firstName && !order.lastName) && "-"}
                    </TableCell>
                    <TableCell className="text-center font-medium">{order.phone || "-"}</TableCell>
                    <TableCell className="text-center font-medium">{order.lineTitle || "-"}</TableCell>
                    <TableCell className="text-center font-medium">{order.lineId || "-"}</TableCell>
                    <TableCell className="text-center font-medium">{Number(order.size || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-center text-green font-semibold">
                      {Number(order.totalPrice || 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan="7" className="text-center text-slate-400 py-12">
                    {loading ? "กำลังโหลดข้อมูล..." : "ไม่พบรายการคำสั่งซื้อในช่วงเวลานี้"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
